import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { NavLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { buttonizeDiv } from '../../util/buttonizeDiv';

import { ReactComponent as DropdownIcon } from '../../assets/images/svg/dropdown.svg';
import { ReactComponent as ArrowIcon } from '../../assets/images/svg/chevron-right.svg';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';
import { mediaBelow, pxToRem } from '../../assets/styles/helper';
import { uFlexCenter, uTransition } from '../../assets/styles/utility';

// Define these first so they can be referenced in MegaMenuContainer
const MegaMenuLabel = styled.span`
  ${uFlexCenter};
  text-decoration: none;
  line-height: 1.5;
  font-size: ${pxToRem(18)};
  color: ${themeColors.colorTextContrast};
  text-transform: uppercase;
  height: 100%;
  width: 100%;
  cursor: pointer;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    color: ${themeColors.colorTextPrimary};
    justify-content: flex-start;
    align-items: center;
    padding: ${pxToRem(14)} ${pxToRem(18)};
    font-size: ${pxToRem(20)};
    line-height: 1.35;
    text-transform: capitalize;

    &:hover {
      background-color: ${themeColors.colorNavigationItemHover};
    }
  }
`;

const DropdownIconWrapper = styled.div`
  margin-left: ${pxToRem(12)};

  svg {
    ${uTransition};
    width: 1rem;
    height: 1rem;
    color: ${themeColors.colorTextContrast};
  }

  ${mediaBelow(variables.breakpoints.bpXl)} {
    margin-left: auto;

    svg {
      color: ${themeColors.colorTextPrimary};
      width: ${pxToRem(18)};
      height: ${pxToRem(18)};
    }
  }
`;

const MegaMenuDropdown = styled.div`
  position: fixed;
  top: ${pxToRem(80)};
  left: 0;
  right: 0;
  width: 100vw;
  background-color: ${themeColors.colorWhite};
  z-index: ${variables.zIndex.indexSm};
  display: none;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  border-radius: ${pxToRem(10)};
  padding: ${pxToRem(24)} ${pxToRem(48)};
  overflow-x: hidden;

  @media (max-width: 1480px) {
    padding: ${pxToRem(24)} ${pxToRem(32)};
  }

  @media (max-width: 1200px) {
    padding: ${pxToRem(20)} ${pxToRem(24)};
  }

  ${mediaBelow(variables.breakpoints.bpXl)} {
    position: absolute;
    top: 100%;
    width: 100%;
    max-width: 100%;
    left: 0;
    right: auto;
    background-color: ${themeColors.colorSecondaryHeaderBackground};
    box-shadow: none;
    border-radius: 0;
    padding: 0;
    margin-top: 0;
  }
`;

const MegaMenuContainer = styled.div`
  ${uFlexCenter};
  position: relative;
  height: 100%;
  width: 100%;

  ${({ isPageOpen, isMobile }) =>
    isPageOpen &&
    !isMobile &&
    `
    ${MegaMenuLabel} {
      font-weight: 600;
    }
  `}

  ${mediaBelow(variables.breakpoints.bpXl)} {
    flex-direction: column;

    ${({ isMobile }) =>
      isMobile &&
      `display: flex;

      ${MegaMenuDropdown} {
        display: none;
      }
    `}

    ${({ isMobileOpen }) =>
      isMobileOpen &&
      `
      ${DropdownIconWrapper} {
        svg {
          transform: rotate(-180deg);
        }
      }

      ${MegaMenuDropdown} {
        display: block;
      }
    `}
  }

  ${({ isDesktopOpen, forceClose }) =>
    forceClose
      ? `
    ${MegaMenuDropdown} {
      display: none !important;
    }
  `
      : isDesktopOpen &&
        `
    ${DropdownIconWrapper} {
      svg {
        transform: rotate(-180deg);
      }
    }

    ${MegaMenuDropdown} {
      display: block;
    }
  `}

  &:hover {
    ${({ forceClose }) =>
      !forceClose &&
      `
      ${DropdownIconWrapper} {
        svg {
          transform: rotate(-180deg);
        }
      }

      ${MegaMenuDropdown} {
        display: block;
      }
    `}
  }

  ${mediaBelow(variables.breakpoints.bpXl)} {
    &:hover {
      ${MegaMenuDropdown} {
        display: none;
      }
    }
  }
`;

const MegaMenuContent = styled.div`
  display: grid;
  grid-template-columns: ${pxToRem(382)} 1fr;
  gap: ${pxToRem(24)};
  min-height: ${pxToRem(484)};
  max-width: ${pxToRem(1392)};
  width: 100%;

  @media (max-width: 1480px) {
    max-width: 100%;
  }

  @media (max-width: 1200px) {
    grid-template-columns: ${pxToRem(300)} 1fr;
    gap: ${pxToRem(16)};
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: ${pxToRem(16)};
  }

  ${mediaBelow(variables.breakpoints.bpXl)} {
    grid-template-columns: 1fr;
    gap: 0;
    min-height: auto;
  }
`;

const FeaturedSection = styled.div`
  background: linear-gradient(135deg, #052d6b 0%, #1c4792 100%);
  border-radius: ${pxToRem(10)};
  padding: ${pxToRem(24)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  overflow: hidden;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    display: none;
  }
`;

const FeaturedTitle = styled.h3`
  font-family: 'Instrument Sans', sans-serif;
  font-weight: 600;
  font-size: ${pxToRem(24)};
  line-height: 1.3;
  color: ${themeColors.colorWhite};
  margin: 0 0 ${pxToRem(16)} 0;
  opacity: 0.8;
`;

const FeaturedText = styled.p`
  font-family: 'Instrument Sans', sans-serif;
  font-weight: 400;
  font-size: ${pxToRem(18)};
  line-height: 1.3;
  color: ${themeColors.colorWhite};
  margin: 0;
`;

const FeaturedButton = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${themeColors.colorWhite};
  border: 1px solid #052d6b;
  color: #052d6b;
  padding: ${pxToRem(12)} ${pxToRem(16)};
  border-radius: ${pxToRem(5)};
  font-family: 'Instrument Sans', sans-serif;
  font-weight: 500;
  font-size: ${pxToRem(18)};
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #052d6b;
    color: ${themeColors.colorWhite};
    border-color: ${themeColors.colorWhite};

    svg {
      color: ${themeColors.colorWhite};
    }
  }

  svg {
    width: ${pxToRem(15)};
    height: ${pxToRem(15)};
    color: #052d6b;
    transition: color 0.2s ease;
  }
`;

const BenefitsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(24)};

  ${mediaBelow(variables.breakpoints.bpXl)} {
    padding: ${pxToRem(10)} ${pxToRem(36)};
  }
`;

const BenefitsTitle = styled.h3`
  font-family: 'Instrument Sans', sans-serif;
  font-weight: 600;
  font-size: ${pxToRem(24)};
  line-height: 1.3;
  color: #3d3d3d;
  text-align: center;
  margin: ${pxToRem(15)} 0;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    font-size: ${pxToRem(20)};
    margin: ${pxToRem(10)} 0;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${pxToRem(24)};

  ${mediaBelow(variables.breakpoints.bpXl)} {
    grid-template-columns: 1fr;
    gap: ${pxToRem(16)};
  }
`;

const BenefitCard = styled.div`
  background-color: ${themeColors.colorWhite};
  border: 1px solid
    ${({ $highlighted }) => ($highlighted ? '#052d6b' : '#e3e3e3')};
  border-radius: ${pxToRem(25)};
  padding: ${pxToRem(16)} ${pxToRem(24)};
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(8)};
  min-height: ${pxToRem(154)};
  cursor: pointer;
  transition: all 0.3s ease;
  ${({ $highlighted }) =>
    $highlighted &&
    `
    box-shadow: 3px 6px 12px 0px rgba(0, 0, 0, 0.15);
  `}

  &:hover {
    border-color: #052d6b;
    box-shadow: 3px 6px 12px 0px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
    background-color: #f8f9fa;
  }

  ${mediaBelow(variables.breakpoints.bpXl)} {
    border-radius: ${pxToRem(10)};
    padding: ${pxToRem(12)} ${pxToRem(16)};
    min-height: auto;
  }
`;

const BenefitHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(16)};
`;

const BenefitTitle = styled.h4`
  font-family: 'Instrument Sans', sans-serif;
  font-weight: 600;
  font-size: ${pxToRem(20)};
  line-height: 1.3;
  color: #052d6b;
  margin: 0;
  text-align: center;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    font-size: ${pxToRem(18)};
    text-align: left;
  }
`;

const BenefitIconWrapper = styled.div`
  width: ${pxToRem(42)};
  height: ${pxToRem(42)};
  min-width: ${pxToRem(42)};
  border-radius: ${pxToRem(39)};
  border: 1px solid #052d6b;
  background-color: ${themeColors.colorWhite};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: ${pxToRem(28)};
    height: ${pxToRem(28)};
    color: #052d6b;
  }
`;

const BenefitDescription = styled.p`
  font-family: 'Instrument Sans', sans-serif;
  font-weight: 400;
  font-size: ${pxToRem(16)};
  line-height: 1.3;
  color: #3d3d3d;
  margin: 0;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    font-size: ${pxToRem(14)};
  }
`;

// Simple icon components using SVG paths
const DollarIcon = () => (
  <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14 2.625C7.65 2.625 2.625 7.65 2.625 14C2.625 20.35 7.65 25.375 14 25.375C20.35 25.375 25.375 20.35 25.375 14C25.375 7.65 20.35 2.625 14 2.625ZM14 23.625C8.61875 23.625 4.375 19.3813 4.375 14C4.375 8.61875 8.61875 4.375 14 4.375C19.3813 4.375 23.625 8.61875 23.625 14C23.625 19.3813 19.3813 23.625 14 23.625Z"
      fill="currentColor"
    />
    <path
      d="M15.75 18.375H14.875V19.25H13.125V18.375H11.375C10.8937 18.375 10.5 17.9813 10.5 17.5C10.5 17.0187 10.8937 16.625 11.375 16.625H15.75C16.2313 16.625 16.625 16.2313 16.625 15.75C16.625 15.2687 16.2313 14.875 15.75 14.875H12.25C10.8062 14.875 9.625 13.6938 9.625 12.25C9.625 10.8062 10.8062 9.625 12.25 9.625H13.125V8.75H14.875V9.625H16.625C17.1063 9.625 17.5 10.0187 17.5 10.5C17.5 10.9813 17.1063 11.375 16.625 11.375H12.25C11.7687 11.375 11.375 11.7687 11.375 12.25C11.375 12.7313 11.7687 13.125 12.25 13.125H15.75C17.1938 13.125 18.375 14.3062 18.375 15.75C18.375 17.1938 17.1938 18.375 15.75 18.375Z"
      fill="currentColor"
    />
  </svg>
);

const BookIcon = () => (
  <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M23.625 3.5H8.75C6.8625 3.5 5.25 5.1125 5.25 7V21C5.25 22.8875 6.8625 24.5 8.75 24.5H23.625C24.1063 24.5 24.5 24.1063 24.5 23.625V4.375C24.5 3.89375 24.1063 3.5 23.625 3.5ZM22.75 22.75H8.75C7.83125 22.75 7 21.9187 7 21C7 20.0812 7.83125 19.25 8.75 19.25H22.75V22.75ZM22.75 17.5H8.75C8.225 17.5 7.73125 17.6375 7.29375 17.8625C7.1125 17.15 7 16.4025 7 15.75V7C7 6.08125 7.83125 5.25 8.75 5.25H22.75V17.5Z"
      fill="currentColor"
    />
  </svg>
);

const NewsIcon = () => (
  <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24.5 5.25H3.5C2.53125 5.25 1.75 6.03125 1.75 7V21C1.75 21.9688 2.53125 22.75 3.5 22.75H24.5C25.4688 22.75 26.25 21.9688 26.25 21V7C26.25 6.03125 25.4688 5.25 24.5 5.25ZM24.5 21H3.5V7H24.5V21Z"
      fill="currentColor"
    />
    <path
      d="M7 10.5H12.25V15.75H7V10.5ZM14 10.5H21V12.25H14V10.5ZM14 14H21V15.75H14V14ZM7 17.5H21V19.25H7V17.5Z"
      fill="currentColor"
    />
  </svg>
);

const VideoIcon = () => (
  <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24.0625 7.4375L18.375 11.375V8.75C18.375 7.29375 17.2062 6.125 15.75 6.125H5.25C3.79375 6.125 2.625 7.29375 2.625 8.75V19.25C2.625 20.7062 3.79375 21.875 5.25 21.875H15.75C17.2062 21.875 18.375 20.7062 18.375 19.25V16.625L24.0625 20.5625C24.9375 21.175 26.25 20.5625 26.25 19.4688V8.53125C26.25 7.4375 24.9375 6.825 24.0625 7.4375ZM16.625 19.25C16.625 19.7312 16.2312 20.125 15.75 20.125H5.25C4.76875 20.125 4.375 19.7312 4.375 19.25V8.75C4.375 8.26875 4.76875 7.875 5.25 7.875H15.75C16.2312 7.875 16.625 8.26875 16.625 8.75V19.25ZM24.5 19.4688L18.375 15.3125V12.6875L24.5 8.53125V19.4688Z"
      fill="currentColor"
    />
  </svg>
);

const InsiderPlusMegaMenu = ({
  title,
  isNavOpen,
  closeNav,
  isMobile,
  dataCyLabel,
}) => {
  const [isOpen, setIsOpen] = useState(isMobile);
  const [isPageOpen, setIsPageOpen] = useState(false);
  const [forceClose, setForceClose] = useState(false);
  const location = useLocation();

  const handleCloseAndNavigate = () => {
    setIsOpen(false);
    setForceClose(true);
    closeNav();

    // Reset forceClose after a brief delay to allow hover to work again
    setTimeout(() => {
      setForceClose(false);
    }, 300);
  };

  useEffect(() => {
    if (isNavOpen) {
      setIsOpen(false);
    }
  }, [isNavOpen, isMobile]);

  useEffect(() => {
    // Check if we're on any Insider+ related page
    if (
      location.pathname === '/insider-plus' ||
      location.pathname === '/insider-plus-welcome'
    ) {
      setIsPageOpen(true);
    } else {
      setIsPageOpen(false);
    }
  }, [location.pathname]);

  return (
    <MegaMenuContainer
      isPageOpen={isPageOpen}
      isMobile={isMobile}
      isMobileOpen={!isOpen && isMobile && isNavOpen}
      forceClose={forceClose}
      {...buttonizeDiv(() => {
        setIsOpen(!isOpen);
      })}
      data-cy={dataCyLabel}
    >
      <MegaMenuLabel>
        {title}
        <DropdownIconWrapper>
          <DropdownIcon />
        </DropdownIconWrapper>
      </MegaMenuLabel>
      <MegaMenuDropdown>
        <MegaMenuContent>
          <FeaturedSection>
            <div>
              <FeaturedTitle>Recommended For You</FeaturedTitle>
              <FeaturedText>
                Based on your interests in gold investing, check out our new
                Silver Market Trends report.
              </FeaturedText>
            </div>
            <FeaturedButton
              to="/insider-plus-welcome"
              onClick={handleCloseAndNavigate}
              data-cy="nav-insider-plus-go-to-lounge"
            >
              Go to Lounge
              <ArrowIcon />
            </FeaturedButton>
          </FeaturedSection>

          <BenefitsSection>
            <BenefitsTitle>Explore Your Benefits</BenefitsTitle>
            <BenefitsGrid>
              <BenefitCard>
                <BenefitHeader>
                  <BenefitTitle>$100 Free Credit</BenefitTitle>
                  <BenefitIconWrapper>
                    <DollarIcon />
                  </BenefitIconWrapper>
                </BenefitHeader>
                <BenefitDescription>
                  Use your credit for storage purchases
                </BenefitDescription>
              </BenefitCard>

              <BenefitCard>
                <BenefitHeader>
                  <BenefitTitle>Books</BenefitTitle>
                  <BenefitIconWrapper>
                    <BookIcon />
                  </BenefitIconWrapper>
                </BenefitHeader>
                <BenefitDescription>
                  Claim or gift Mike&apos;s Guide to Investing in Gold & Silver
                  — enduring insight in every page.
                </BenefitDescription>
              </BenefitCard>

              <BenefitCard>
                <BenefitHeader>
                  <BenefitTitle>Newsletter</BenefitTitle>
                  <BenefitIconWrapper>
                    <NewsIcon />
                  </BenefitIconWrapper>
                </BenefitHeader>
                <BenefitDescription>
                  Market signals, allocation ideas, and charts
                </BenefitDescription>
              </BenefitCard>

              <BenefitCard>
                <BenefitHeader>
                  <BenefitTitle>
                    Livestreams and Question & Answer Sessions
                  </BenefitTitle>
                  <BenefitIconWrapper>
                    <VideoIcon />
                  </BenefitIconWrapper>
                </BenefitHeader>
                <BenefitDescription>
                  Monthly sessions led by experts
                </BenefitDescription>
              </BenefitCard>
            </BenefitsGrid>
          </BenefitsSection>
        </MegaMenuContent>
      </MegaMenuDropdown>
    </MegaMenuContainer>
  );
};

InsiderPlusMegaMenu.propTypes = {
  title: PropTypes.string,
  isNavOpen: PropTypes.bool,
  closeNav: PropTypes.func,
  isMobile: PropTypes.bool,
  dataCyLabel: PropTypes.string,
};

export default InsiderPlusMegaMenu;
